/**
 * Requirement authority and precedence.
 *
 * Contract: docs/source-of-truth/worker-service-delivery-architecture.md
 * ("External obligation boundary" and "Configuration hierarchy").
 *
 * The single binding rule this module enforces: resolution is **additive and
 * restrictive**. A lower-authority layer may add a requirement, or tighten an
 * existing one, or hide an irrelevant optional field. It may never remove,
 * downgrade or waive a requirement owned by a higher authority.
 *
 * This is the mechanism that stops a tenant policy from switching off a
 * statutory or program obligation.
 */

// ---------------------------------------------------------------------------
// Authority classes
// ---------------------------------------------------------------------------

/**
 * Ordered strongest-first. The array order IS the precedence order; index is
 * the rank. Do not reorder without a migration of stored precedence decisions.
 */
export const REQUIREMENT_AUTHORITIES = [
  'STATUTORY',
  'PROGRAM',
  'PROFESSIONAL_SCOPE',
  'PROVIDER_POLICY',
  'SERVICE_CONTRACT',
  'PLAN_CLIENT',
  'OPTIONAL_CAPTURE',
] as const;

export type RequirementAuthority = (typeof REQUIREMENT_AUTHORITIES)[number];

const REQUIREMENT_AUTHORITY_SET: ReadonlySet<string> = new Set(REQUIREMENT_AUTHORITIES);

export function isRequirementAuthority(
  value: string | null | undefined,
): value is RequirementAuthority {
  return typeof value === 'string' && REQUIREMENT_AUTHORITY_SET.has(value);
}

/**
 * Lower rank number = stronger authority. `STATUTORY` is 0.
 */
export function requirementAuthorityRank(authority: RequirementAuthority): number {
  return REQUIREMENT_AUTHORITIES.indexOf(authority);
}

export function isStrongerAuthority(
  candidate: RequirementAuthority,
  incumbent: RequirementAuthority,
): boolean {
  return requirementAuthorityRank(candidate) < requirementAuthorityRank(incumbent);
}

// ---------------------------------------------------------------------------
// Waivability
// ---------------------------------------------------------------------------

/**
 * Authorities a tenant may never waive, weaken or except away. A failure under
 * one of these blocks delivery; the only remaining path is the emergency /
 * incident pathway, which is not an ordinary completion.
 */
const NON_WAIVABLE_AUTHORITIES: ReadonlySet<RequirementAuthority> = new Set([
  'STATUTORY',
  'PROGRAM',
  'PROFESSIONAL_SCOPE',
]);

export function isTenantWaivableAuthority(authority: RequirementAuthority): boolean {
  return !NON_WAIVABLE_AUTHORITIES.has(authority);
}

/**
 * Only `PROVIDER_POLICY` supports the enumerated, exact-slot, expiring manager
 * exception. `SERVICE_CONTRACT` and `PLAN_CLIENT` are configured through their
 * own governed authoring paths rather than a point-of-care override, and
 * `OPTIONAL_CAPTURE` never blocks so never needs one.
 */
export function supportsExactSlotException(authority: RequirementAuthority): boolean {
  return authority === 'PROVIDER_POLICY';
}

// ---------------------------------------------------------------------------
// Configuration layers
// ---------------------------------------------------------------------------

/**
 * The resolution order from the architecture's "Configuration hierarchy".
 * Applied in array order; each layer may only add or tighten.
 */
export const CONFIGURATION_LAYERS = [
  'SYSTEM_BASELINE',
  'SERVICE_BASE_PROFILE',
  'EXECUTION_MODE_PROFILE',
  'PROFESSIONAL_DELEGATION_PROFILE',
  'PATHWAY_RISK_OVERLAY',
  'PROVIDER_POLICY_ADDITION',
  'SERVICE_CONTRACT_REQUIREMENT',
  'PLAN_CLIENT_INSTRUCTION',
  'PRESENTATION_PREFERENCE',
] as const;

export type ConfigurationLayer = (typeof CONFIGURATION_LAYERS)[number];

export function configurationLayerRank(layer: ConfigurationLayer): number {
  return CONFIGURATION_LAYERS.indexOf(layer);
}

/**
 * The strongest authority a given configuration layer is permitted to assert.
 * A layer that tries to author a requirement above its ceiling is a profile
 * publication error, not a runtime downgrade.
 */
const LAYER_AUTHORITY_CEILING: Readonly<Record<ConfigurationLayer, RequirementAuthority>> = {
  SYSTEM_BASELINE: 'STATUTORY',
  SERVICE_BASE_PROFILE: 'PROGRAM',
  EXECUTION_MODE_PROFILE: 'PROGRAM',
  PROFESSIONAL_DELEGATION_PROFILE: 'PROFESSIONAL_SCOPE',
  PATHWAY_RISK_OVERLAY: 'PROFESSIONAL_SCOPE',
  PROVIDER_POLICY_ADDITION: 'PROVIDER_POLICY',
  SERVICE_CONTRACT_REQUIREMENT: 'SERVICE_CONTRACT',
  PLAN_CLIENT_INSTRUCTION: 'PLAN_CLIENT',
  PRESENTATION_PREFERENCE: 'OPTIONAL_CAPTURE',
};

export function maxAuthorityForLayer(layer: ConfigurationLayer): RequirementAuthority {
  return LAYER_AUTHORITY_CEILING[layer];
}

export function canLayerAssertAuthority(
  layer: ConfigurationLayer,
  authority: RequirementAuthority,
): boolean {
  return (
    requirementAuthorityRank(authority) >= requirementAuthorityRank(maxAuthorityForLayer(layer))
  );
}
